import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Search as SearchIcon, SlidersHorizontal, X, Bookmark } from "lucide-react";
import api, { CONDITIONS, DELIVERY } from "@/lib/api";
import { ListingCard } from "@/components/ListingCard";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

const LIMIT = 12;

// Fetches listings for the current query and manages pagination/loading.
function useListingSearch(buildQuery) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setPage(1);
    api.get(`/listings?${buildQuery(1)}`).then((r) => {
      setItems(r.data.items); setTotal(r.data.total); setHasMore(r.data.has_more);
    }).finally(() => setLoading(false));
  }, [buildQuery]);

  const loadMore = () => {
    const next = page + 1;
    api.get(`/listings?${buildQuery(next)}`).then((r) => {
      setItems((prev) => [...prev, ...r.data.items]); setHasMore(r.data.has_more); setPage(next);
    });
  };

  return { items, total, hasMore, loading, loadMore };
}

export default function Browse() {
  const [params, setParams] = useSearchParams();
  const { isAuthed } = useAuth();
  const [categories, setCategories] = useState([]);
  const [term, setTerm] = useState(params.get("q") || "");
  const [sugs, setSugs] = useState([]);

  const category = params.get("category") || "all";
  const subcategory = params.get("subcategory") || "";
  const sort = params.get("sort") || "recent";
  const q = params.get("q") || "";
  const priceMin = params.get("price_min") || "";
  const priceMax = params.get("price_max") || "";
  const condition = params.get("condition") || "";
  const delivery = params.get("delivery") || "";
  const sellerType = params.get("seller_type") || "";
  const dateDays = params.get("date_days") || "";

  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)); }, []);

  const buildQuery = useCallback((pg) => {
    const query = new URLSearchParams();
    if (category !== "all") query.set("category", category);
    if (subcategory) query.set("subcategory", subcategory);
    if (q) query.set("q", q);
    if (priceMin) query.set("price_min", priceMin);
    if (priceMax) query.set("price_max", priceMax);
    if (condition) query.set("condition", condition);
    if (delivery) query.set("delivery", delivery);
    if (sellerType) query.set("seller_type", sellerType);
    if (dateDays) query.set("date_days", dateDays);
    query.set("sort", sort);
    query.set("page", pg);
    query.set("limit", LIMIT);
    return query.toString();
  }, [category, subcategory, q, priceMin, priceMax, condition, delivery, sellerType, dateDays, sort]);

  const { items, total, hasMore, loading, loadMore } = useListingSearch(buildQuery);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value && value !== "all") next.set(key, value); else next.delete(key);
    setParams(next);
  };

  // Switching the main category always clears any active subcategory.
  const selectCategory = (value) => {
    const next = new URLSearchParams(params);
    if (value && value !== "all") next.set("category", value); else next.delete("category");
    next.delete("subcategory");
    setParams(next);
  };

  const selectedCat = categories.find((c) => c.slug === category);
  const subOptions = selectedCat?.subs || [];

  const submitSearch = (e) => { e.preventDefault(); updateParam("q", term); setSugs([]); };
  const onTermChange = (v) => {
    setTerm(v);
    if (v.length >= 2) api.get(`/search/suggestions?q=${encodeURIComponent(v)}`).then((r) => setSugs(r.data)); else setSugs([]);
  };

  const clearFilters = () => setParams(category !== "all" ? { category } : {});

  const saveSearch = async () => {
    if (!isAuthed) return toast.error("Log in om zoekopdrachten op te slaan");
    await api.post("/saved-searches", { query: q, category, filters: { priceMin, priceMax, condition, delivery, sellerType } });
    toast.success("Zoekopdracht opgeslagen");
  };

  const title = useMemo(() => {
    if (q) return `Resultaten voor "${q}"`;
    const c = categories.find((c) => c.slug === category);
    if (c && subcategory) {
      const s = (c.subs || []).find((s) => s.slug === subcategory);
      if (s) return s.name;
    }
    return c ? c.name : "Alle advertenties";
  }, [q, category, subcategory, categories]);

  const activeFilterCount = [priceMin, priceMax, condition, delivery, sellerType, dateDays].filter(Boolean).length;

  return (
    <div className="mx-auto max-w-7xl px-4 pb-24 pt-6 md:px-8 md:pb-12">
      <div className="no-scrollbar mb-5 flex gap-2 overflow-x-auto pb-1">
        <Chip active={category === "all"} onClick={() => selectCategory("all")} testid="chip-all">Alles</Chip>
        {categories.map((c) => <Chip key={c.slug} active={category === c.slug} onClick={() => selectCategory(c.slug)} testid={`chip-${c.slug}`}>{c.name}</Chip>)}
      </div>

      {subOptions.length > 0 && (
        <div className="no-scrollbar mb-5 flex gap-2 overflow-x-auto pb-1" data-testid="subcategory-row">
          <SubChip active={!subcategory} onClick={() => updateParam("subcategory", "")} testid="subchip-all">Alle</SubChip>
          {subOptions.map((s) => <SubChip key={s.slug} active={subcategory === s.slug} onClick={() => updateParam("subcategory", s.slug)} testid={`subchip-${s.slug}`}>{s.name}</SubChip>)}
        </div>
      )}

      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl text-[#F5F5F5] sm:text-3xl" data-testid="browse-title">{title} <span className="text-base text-[#777]">({total})</span></h1>
        <div className="flex items-center gap-2">
          <Sheet>
            <SheetTrigger asChild>
              <button data-testid="open-filters-btn" className="relative flex items-center gap-2 rounded-full border border-[#333] bg-[#1c1c1c] px-4 py-2.5 text-sm font-semibold text-[#F5F5F5] hover:border-[#FF6A00]">
                <SlidersHorizontal className="h-4 w-4" /> Filters
                {activeFilterCount > 0 && <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#FF6A00] text-[10px] font-bold text-[#111]">{activeFilterCount}</span>}
              </button>
            </SheetTrigger>
            <SheetContent className="w-full overflow-y-auto border-[#333] bg-[#111] text-[#F5F5F5] sm:max-w-md">
              <SheetHeader><SheetTitle className="font-display text-2xl text-[#F5F5F5]">FILTERS</SheetTitle></SheetHeader>
              <div className="mt-6 space-y-6">
                <FilterBlock label="Prijs (€)">
                  <div className="flex items-center gap-2">
                    <input data-testid="filter-price-min" type="number" value={priceMin} onChange={(e) => updateParam("price_min", e.target.value)} placeholder="Min" className={inputSm} />
                    <span className="text-[#777]">—</span>
                    <input data-testid="filter-price-max" type="number" value={priceMax} onChange={(e) => updateParam("price_max", e.target.value)} placeholder="Max" className={inputSm} />
                  </div>
                </FilterBlock>
                <FilterBlock label="Staat">
                  <FilterSelect testid="filter-condition" value={condition} onChange={(v) => updateParam("condition", v)} placeholder="Alle" options={CONDITIONS} />
                </FilterBlock>
                <FilterBlock label="Levering">
                  <FilterSelect testid="filter-delivery" value={delivery} onChange={(v) => updateParam("delivery", v)} placeholder="Alle" options={DELIVERY} />
                </FilterBlock>
                <FilterBlock label="Verkoper">
                  <FilterSelect testid="filter-seller-type" value={sellerType} onChange={(v) => updateParam("seller_type", v)} placeholder="Alle" options={[{ value: "particulier", label: "Particulier" }, { value: "zakelijk", label: "Zakelijk" }]} />
                </FilterBlock>
                <FilterBlock label="Geplaatst">
                  <FilterSelect testid="filter-date" value={dateDays} onChange={(v) => updateParam("date_days", v)} placeholder="Altijd" options={[{ value: "1", label: "Afgelopen 24 uur" }, { value: "7", label: "Afgelopen week" }, { value: "30", label: "Afgelopen maand" }]} />
                </FilterBlock>
                <div className="flex gap-2">
                  <button onClick={clearFilters} data-testid="clear-filters-btn" className="flex flex-1 items-center justify-center gap-2 rounded-full border border-[#333] py-3 font-semibold text-[#F5F5F5] hover:border-[#FF6A00]"><X className="h-4 w-4" /> Filters wissen</button>
                  <button onClick={saveSearch} data-testid="save-search-btn" className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[#FF6A00] py-3 font-bold text-[#111]"><Bookmark className="h-4 w-4" /> Bewaar zoekopdracht</button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
          <Select value={sort} onValueChange={(v) => updateParam("sort", v)}>
            <SelectTrigger data-testid="sort-select" className="w-[170px] border-[#333] bg-[#2A2A2A] text-[#F5F5F5]"><SelectValue /></SelectTrigger>
            <SelectContent className="border-[#333] bg-[#1c1c1c] text-[#F5F5F5]">
              <SelectItem value="relevant">Meest relevant</SelectItem>
              <SelectItem value="recent">Nieuwste eerst</SelectItem>
              <SelectItem value="price_asc">Prijs: laag → hoog</SelectItem>
              <SelectItem value="price_desc">Prijs: hoog → laag</SelectItem>
              <SelectItem value="closest">Dichtstbijzijnd</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">{["s1","s2","s3","s4","s5","s6","s7","s8"].map((k) => <div key={k} className="aspect-[4/3] animate-pulse rounded-xl border border-[#333] bg-[#1c1c1c]" />)}</div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-[#333] bg-[#1c1c1c] py-20 text-center" data-testid="browse-empty">
          <p className="font-display text-2xl text-[#F5F5F5]">Niets gevonden</p>
          <p className="mt-2 text-[#A0A0A0]">Probeer een andere zoekterm of pas je filters aan.</p>
          {activeFilterCount > 0 && <button onClick={clearFilters} className="mt-4 rounded-full bg-[#FF6A00] px-5 py-2.5 font-bold text-[#111]">Filters wissen</button>}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="browse-grid">{items.map((l, i) => <ListingCard key={l.id} listing={l} priority={i < 4} />)}</div>
          {hasMore && <div className="mt-8 flex justify-center"><button onClick={loadMore} data-testid="load-more-btn" className="rounded-full border border-[#333] px-8 py-3 font-semibold text-[#F5F5F5] hover:border-[#FF6A00] hover:text-[#FF6A00]">Meer laden</button></div>}
        </>
      )}
    </div>
  );
}

const inputSm = "w-full rounded-lg border border-[#333] bg-[#2A2A2A] p-2.5 text-[#F5F5F5] outline-none focus:border-[#FF6A00]";

function FilterBlock({ label, children }) {
  return <div><p className="mb-2 text-sm font-semibold text-[#F5F5F5]">{label}</p>{children}</div>;
}

function FilterSelect({ value, onChange, placeholder, options, testid }) {
  return (
    <Select value={value || "__all"} onValueChange={(v) => onChange(v === "__all" ? "" : v)}>
      <SelectTrigger data-testid={testid} className="border-[#333] bg-[#2A2A2A] text-[#F5F5F5]"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="border-[#333] bg-[#1c1c1c] text-[#F5F5F5]">
        <SelectItem value="__all">{placeholder}</SelectItem>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function Chip({ children, active, onClick, testid }) {
  return <button data-testid={testid} onClick={onClick} className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${active ? "border-[#FF6A00] bg-[#FF6A00] text-[#111]" : "border-[#333] bg-[#1c1c1c] text-[#F5F5F5] hover:border-[#FF6A00]"}`}>{children}</button>;
}

function SubChip({ children, active, onClick, testid }) {
  return <button data-testid={testid} onClick={onClick} className={`whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${active ? "border-[#FF6A00] bg-[#FF6A00]/15 text-[#FF6A00]" : "border-[#333] bg-transparent text-[#B4B4B4] hover:border-[#FF6A00] hover:text-[#F5F5F5]"}`}>{children}</button>;
}
